import Slider from '../lib/Slider.js';

export default {
    dependencies: ['$element', 'radio'],
    callable(ele, radio) {
        const inputEle = ele.querySelector("input[type='number']");
        const hasPosition = ele.hasAttribute('data-has-position');
        let slider;
        radio('geo-position-changed-from-filters').subscribe(
            handleLocationChanged
        );

        if (hasPosition) {
            slider = createSlider();
        }

        function createSlider() {
            const sliderOptions = {};
            sliderOptions.container = ele.querySelector('.finn-slider');
            sliderOptions.formatter = v =>
                v >= 2000 ? Math.floor(Number(v) / 1000) : v;
            sliderOptions.suffixformatter = v => (v >= 2000 ? 'km' : 'm');
            // These numbers are found by empirical testing.
            const roundingIntervals = [
                [1000, 250],
                [2000, 500],
                [5000, 1000],
                [7000, 2000],
                [10000, 3000],
                [20000, 5000],
                [30000, 10000],
                [60000, 30000],
                [100000, 40000],
            ];
            const conversionConstant = 0.4;
            const inverseConversionConstant = 2.5;
            const conversionFunctionStartValue = 1000;

            sliderOptions.distribution = getSliderDistributionWith(
                roundingIntervals,
                conversionFunctionStartValue,
                conversionConstant,
                inverseConversionConstant
            );
            sliderOptions.snap = true;

            const slider = new Slider(inputEle, null, sliderOptions);

            markIfTouched();

            slider.addEventListener('change', handleRadiusChanged);
            slider.addEventListener('change', markIfTouched);

            return slider;
        }

        function handleLocationChanged(lat, lon, radius) {
            inputEle.value = radius || inputEle.value;
            if (!slider) {
                slider = createSlider();
            }
            const isLocationChanged = lat && lon;
            ele.classList.toggle('hidden', !isLocationChanged);
            slider.layout();
        }

        function handleRadiusChanged(event) {
            event.preventDefault();
            radio('geo-radius-changed-from-filters').broadcast(inputEle.value);
        }

        function markIfTouched() {
            const isTouched = inputEle.value !== '';
            const headingEle = ele.previousElementSibling;
            const trackEle = ele.querySelector('.finn-slider-track-inner');
            if (headingEle) {
                headingEle.classList.toggle('pea', isTouched);
            }
            if (trackEle) {
                trackEle.classList.toggle('bg-pea', isTouched);
            }
        }

        // roundingIntervals :            Array structure: [intervalUpToLimit, intervalStepValue].
        //                                Decides how values are rounded when you slide a thumb on the slider.
        // conversionFunctionStartValue : If the slider has a minimum value higher than zero, this can be adjusted for so the
        //                                mathematical non-linear conversion-function (that distributes the values within the
        //                                slider) starts at x-value zero. Adjusting a slider slider with minimum value 2000 by 2000 gives
        //                                you the y-value for x-value 0 (0=4000-2000).
        // conversionConstant :           Tweeking this contant will give you different distribution of values within the slider.
        // inverseConversionConstant :    Number is used to calculate the inverse of the fromBaseToConverted-function.
        // eslint-disable-next-line max-len
        function getSliderDistributionWith(
            roundingIntervals,
            conversionFunctionStartValue,
            conversionConstant,
            inverseConversionConstant
        ) {
            function getIntervalStepFor(baseValueRounded, limitCheckCallback) {
                const validIntervals = roundingIntervals.filter(interval => {
                    const upToLimit = interval[0];
                    return limitCheckCallback(baseValueRounded, upToLimit);
                });

                const firstInterval = validIntervals[0];
                const defaultStep = 100;
                return firstInterval == null ? defaultStep : firstInterval[1];
            }

            function roundToClosestIntervalStep(
                baseValueRounded,
                intervalStep
            ) {
                const valueHitIntervalStep =
                    baseValueRounded % intervalStep === 0;

                if (valueHitIntervalStep) {
                    return baseValueRounded;
                } else {
                    const roundedFactor = Math.round(
                        baseValueRounded / intervalStep
                    );
                    return roundedFactor * intervalStep;
                }
            }

            const distribution = {
                fromBaseToConverted(value, minValue) {
                    if (Math.abs(value) === Infinity) {
                        return value;
                    } else if (value < 0) {
                        return 0;
                    } else {
                        const adjustingValue = Math.max(
                            minValue - conversionFunctionStartValue,
                            minValue
                        );
                        value -= adjustingValue;
                        return Math.pow(value, conversionConstant);
                    }
                },
                fromConvertedToBase(value, minValue) {
                    // The inverse function of the fromBaseToConverted-function.
                    if (Math.abs(value) === Infinity) {
                        return value;
                    } else if (value === 0) {
                        // Zero to be able to convert values back to base value.
                        return 0;
                    } else {
                        const baseValueWithoutAdjustment = Math.pow(
                            value,
                            inverseConversionConstant
                        );
                        const adjustingValue = Math.max(
                            minValue - conversionFunctionStartValue,
                            minValue
                        );
                        return baseValueWithoutAdjustment + adjustingValue;
                    }
                },
                roundBaseValue(baseValue) {
                    if (Math.abs(baseValue) === Infinity) {
                        return baseValue;
                    }
                    const baseValueRounded = Math.round(baseValue);
                    const intervalStep = getIntervalStepFor(
                        baseValueRounded,
                        (baseValueRounded, upToLimit) =>
                            baseValueRounded < upToLimit
                    );
                    return roundToClosestIntervalStep(
                        baseValueRounded,
                        intervalStep
                    );
                },
                nextBaseValue(baseValue, nextSteps) {
                    if (Math.abs(baseValue) === Infinity) {
                        return baseValue;
                    }
                    let newRoundedBaseValue = Math.round(baseValue);

                    for (let iStep = 1; iStep <= nextSteps; iStep++) {
                        const intervalStep = getIntervalStepFor(
                            newRoundedBaseValue,
                            // eslint-disable-next-line no-loop-func
                            (baseValueRounded, upToLimit) =>
                                newRoundedBaseValue < upToLimit
                        );
                        newRoundedBaseValue += intervalStep;
                    }
                    return newRoundedBaseValue;
                },
                previousBaseValue(baseValue, previousSteps) {
                    if (Math.abs(baseValue) === Infinity) {
                        return baseValue;
                    }
                    let newRoundedBaseValue = Math.round(baseValue);

                    for (let iStep = 1; iStep <= previousSteps; iStep++) {
                        const intervalStep = getIntervalStepFor(
                            newRoundedBaseValue,
                            // eslint-disable-next-line no-loop-func
                            (baseValueRounded, upToLimit) =>
                                newRoundedBaseValue <= upToLimit
                        );
                        newRoundedBaseValue -= intervalStep;
                    }
                    return newRoundedBaseValue;
                },
            };

            return distribution;
        }
    },
};



// WEBPACK FOOTER //
// ./controllers/geoRadius.js